// An animated, step-by-step walkthrough of the health module, embedded at the
// top of the Guide tab. Built natively for the portal (no external runtime):
// self-contained React, the module's font, the module palette. Keyframes live
// in health.css (gw-*); everything else is inline so the piece is portable —
// this is the pattern we'll reuse for other portals' guides.
//
// Auto-play tour: a play/pause control auto-advances; Back/Next and the
// progress dots also drive it. Starts paused, so motion is opt-in (the module's
// prefers-reduced-motion rule collapses the animations to a still regardless).

import { useEffect, useState, type CSSProperties, type JSX, type ReactNode } from 'react';

const AUTOPLAY_SECONDS = 10;

// Palette (drawn from the module; kept inline so the component is portable).
const C = {
  green: '#5c7e25',
  greenDk: '#4a6a1c',
  greenDeep: '#3a5216',
  tint: '#eef3e3',
  note: '#f4f7ec',
  paper: '#fdfbf6',
  field: '#faf9f3',
  field2: '#fcfbf4',
  ink: '#2b3342',
  body: '#565f6e',
  muted: '#6a7280',
  muted2: '#8b93a0',
  muted3: '#9aa1ad',
  line: '#e6e2d5',
  line2: '#eae6da',
  line3: '#ece9df',
  line4: '#e8e5da',
  red: '#b6382d',
  redBg: '#f4dedb',
} as const;

/** Entrance-animation style helper. */
const A = (name: string, delay = 0, dur = '0.55s'): CSSProperties => ({
  animation: `${name} ${dur} both`,
  animationDelay: `${delay}s`,
});

// ── Content ──────────────────────────────────────────────────────────────────
// A segment is plain text, bold, or a chip (outlined control / filled action).
type Seg = string | ['b' | 'c' | 'cf', string];
interface Point {
  n?: number;
  seg: Seg[];
}
interface Step {
  short: string;
  kicker: string;
  title: string;
  lead: Seg[] | string;
  points?: Point[] | null;
  note?: Seg[] | null;
  art: ArtKind;
}
type ArtKind =
  | 'intro'
  | 'launch'
  | 'rule'
  | 'editor'
  | 'editing'
  | 'markings'
  | 'copy'
  | 'ingest'
  | 'publish'
  | 'settings'
  | 'faq';

const STEPS: Step[] = [
  {
    short: 'Overview',
    kicker: 'Church Health Assessment',
    title: 'How this module works',
    lead: 'A quick, friendly tour — launching assessments, editing instruments, the markings that protect people, and what every setting does.',
    art: 'intro',
  },
  {
    short: 'Launch',
    kicker: 'Launching an assessment',
    title: 'Start from Assessments › New',
    lead: "The CRM record is written for you. Three moves and you're live.",
    points: [
      {
        n: 1,
        seg: [
          'Pick the church, the window, the mode and the instrument version. Defaults come from Settings.',
        ],
      },
      {
        n: 2,
        seg: [
          "Enter the facilitator's name and email and press ",
          ['c', 'Check'],
          '. A tick-box fixes a missing Facilitator role on the spot.',
        ],
      },
      {
        n: 3,
        seg: [
          'Press ',
          ['cf', 'Create & choose invitees'],
          ', adjust roles, then ',
          ['cf', 'Add and send invitations'],
          ' or ',
          ['c', 'Save for later'],
          '.',
        ],
      },
    ],
    art: 'launch',
  },
  {
    short: 'The one rule',
    kicker: 'The one rule underneath everything',
    title: 'You always work on a draft',
    lead: [
      'Nothing a respondent sees changes until you commit it — and the editor commits it the safest way for what you changed.',
    ],
    points: [
      { seg: ['A ', ['b', 'wording fix'], ' saves onto the same version, in place.'] },
      { seg: ['Adding a question saves onto the same version too, after a confirmation.'] },
      {
        seg: [
          'Removing or reordering questions publishes a ',
          ['b', 'new version'],
          ', kept separate from the one in use.',
        ],
      },
    ],
    note: ['The rule never bends: answers people have already given are never put at risk.'],
    art: 'rule',
  },
  {
    short: 'Editor',
    kicker: 'The instrument editor',
    title: "Know what you're working on",
    lead: 'The toolbar above the Basis card sets your target.',
    points: [
      { seg: [['b', 'Instrument'], ' picker — which named instrument.'] },
      { seg: [['b', 'Editing from'], ' — which published version your draft starts from.'] },
      {
        seg: [
          ['b', 'Rename'],
          ' changes the catalogue name only — not its versions or their questions.',
        ],
      },
    ],
    note: [
      'Switching instrument or version discards the current draft (it asks first) — the quickest way to start over.',
    ],
    art: 'editor',
  },
  {
    short: 'Editing',
    kicker: 'Editing text & questions',
    title: 'Click anything and type',
    lead: ['Select text for the formatting bar, or use ', ['b', '⌘B ⌘I ⌘U'], '.'],
    points: [
      {
        seg: [
          ['b', '↑ ↓'],
          ' reorder; ',
          ['b', 'Move to…'],
          ' sends a question and its follow-ups elsewhere.',
        ],
      },
      { seg: [['b', '+ Follow-up'], ' adds an indented written question beneath.'] },
      {
        seg: [
          'The group label is presentation only — a heading in the survey. It never affects scoring.',
        ],
      },
    ],
    art: 'editing',
  },
  {
    short: 'Markings',
    kicker: 'Safeguarding & governance',
    title: 'Averages hide rare bad answers',
    lead: 'Nine leaders score a child-safety question 4 and one scores 1 — the 3.7 average looks healthy, yet that one answer matters most.',
    points: [
      {
        seg: [
          ['b', 'Safeguarding'],
          ' questions show one by one and red-flag the moment anyone answers 1 or 2.',
        ],
      },
      {
        seg: [
          ['b', 'Governance'],
          ' questions show one by one too, but never alarm-styled — a task, not a crisis.',
        ],
      },
      { seg: ['Marked questions are exempt from averaging, so a single voice can’t be buried.'] },
    ],
    note: ['Where a question would be both, ', ['b', 'safeguarding wins'], '.'],
    art: 'markings',
  },
  {
    short: 'Copy',
    kicker: 'Copy from another instrument',
    title: 'Reuse what already works',
    lead: ['Press ', ['c', 'Copy from another instrument'], ' at the top of the editor.'],
    points: [
      { n: 1, seg: ['Pick the source instrument and version.'] },
      { n: 2, seg: ['Tick a whole section, or individual questions, and choose where they land.'] },
      { n: 3, seg: ['Press ', ['cf', 'Copy into the draft'], '.'] },
    ],
    note: [
      'Copies are independent — a copied question is a new question with a fresh answer history.',
    ],
    art: 'copy',
  },
  {
    short: 'Ingest',
    kicker: 'Ingest a document',
    title: 'Turn a document into a draft',
    lead: 'Paste from Word, Google Docs, a PDF or an email — plain-text files attach directly.',
    points: [
      { n: 1, seg: ['Press ', ['c', 'Ingest a document'], ' and paste everything in.'] },
      {
        n: 2,
        seg: [
          'Press ',
          ['cf', 'Ingest into a draft'],
          ' — a full instrument takes a minute or two.',
        ],
      },
      {
        n: 3,
        seg: ["Check every section against the original. It's faithful, but not infallible."],
      },
    ],
    note: [
      'Ingesting a document replaces the current draft (it asks first). Nothing is published until you publish it.',
    ],
    art: 'ingest',
  },
  {
    short: 'Publish',
    kicker: 'Publishing a new version',
    title: 'Two targets, existing work untouched',
    lead: 'Publish into the current instrument (a new version in its history) or as a brand-new instrument.',
    points: [
      {
        seg: [
          'A new version ',
          ['b', "doesn't move"],
          ' any assessment already using the old one — running and closed cycles keep the version they started with, so answers stay comparable.',
        ],
      },
      {
        seg: [
          'Ticking ',
          ['b', '“Use this version for newly created cycles”'],
          ' makes it the default for future assessments only.',
        ],
      },
      {
        seg: [
          'To move an ',
          ['b', 'open, unanswered'],
          " assessment onto a new version, switch it on that assessment's Cycle settings tab.",
        ],
      },
    ],
    art: 'publish',
  },
  {
    short: 'Settings',
    kicker: 'Module settings',
    title: 'Defaults that govern everything',
    lead: [
      'Nothing applies until ',
      ['cf', 'Save settings'],
      " is pressed — Save writes exactly what's in the boxes.",
    ],
    points: [
      {
        seg: [
          ['b', 'Module switch'],
          ' — the master switch; off closes every link and stops all email.',
        ],
      },
      {
        seg: [
          ['b', 'Anonymity thresholds'],
          ' — the no-one-can-be-picked-out promise. Raise with care, never lower casually.',
        ],
      },
      {
        seg: [
          ['b', 'Reminders, retention, audit'],
          ' — limits, written-answer purge, and a trail of every admin action.',
        ],
      },
    ],
    art: 'settings',
  },
  {
    short: 'Quick answers',
    kicker: 'Quick answers',
    title: 'Good to know',
    lead: 'The questions people ask most.',
    points: [
      {
        seg: [
          ['b', 'Fix a typo in a published version?'],
          ' Yes — the editor’s ',
          ['c', 'Correct wording'],
          ' saves it onto the same version in place. Adding a question is fine too; only removing or reordering needs a new version.',
        ],
      },
      {
        seg: [
          ['b', 'Made a mess of the draft?'],
          " Re-choose the version in Editing from; it's discarded after a confirm.",
        ],
      },
      {
        seg: [
          ['b', 'Add safeguarding markings to an older version?'],
          ' The editor carries them in from settings and publishes a new version — changing designations affects analysis.',
        ],
      },
      {
        seg: [
          ['b', 'How often can I ingest documents?'],
          ' Six an hour; the button tells you if you hit the limit.',
        ],
      },
    ],
    art: 'faq',
  },
];

// ── Inline text ──────────────────────────────────────────────────────────────
function Chip({ children, filled }: { children: ReactNode; filled?: boolean }): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 7px',
        margin: '0 1px',
        borderRadius: 6,
        fontSize: '0.84em',
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        ...(filled
          ? { background: C.green, color: '#fff' }
          : { background: '#fff', color: '#3a4250', border: '1px solid #d7dae0' }),
      }}
    >
      {children}
    </span>
  );
}

function segs(arr: Seg[] | string): ReactNode {
  if (typeof arr === 'string') return arr;
  return arr.map((s, i) => {
    if (typeof s === 'string') return <span key={i}>{s}</span>;
    const [t, txt] = s;
    if (t === 'b')
      return (
        <strong key={i} style={{ fontWeight: 700, color: C.ink }}>
          {txt}
        </strong>
      );
    return (
      <Chip key={i} filled={t === 'cf'}>
        {txt}
      </Chip>
    );
  });
}

// ── Illustrations ────────────────────────────────────────────────────────────
const stageStyle: CSSProperties = {
  position: 'relative',
  height: 196,
  background: '#fff',
  border: `1px solid ${C.line3}`,
  borderRadius: 12,
  padding: 18,
  overflow: 'hidden',
};
const fieldStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  background: C.field,
  border: `1px solid ${C.line4}`,
  borderRadius: 9,
  fontSize: 13,
  color: '#4b5560',
  fontWeight: 600,
};
const tick = (size = 17, fs = 10): CSSProperties => ({
  width: size,
  height: size,
  borderRadius: '50%',
  background: C.green,
  color: '#fff',
  fontSize: fs,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
});

function Art({ kind }: { kind: ArtKind }): JSX.Element {
  if (kind === 'intro') {
    return (
      <div style={stageStyle}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `calc(50% - 150px + ${i * 14}px)`,
              top: 36 + i * 12,
              width: 300,
              height: 116,
              background: '#fbfaf4',
              border: `1px solid ${C.line3}`,
              borderRadius: 12,
              ...A('gw-rise', 0.05 + i * 0.1),
            }}
          />
        ))}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            margin: 'auto',
            width: 70,
            height: 70,
            borderRadius: 19,
            background: C.green,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 12px 26px -10px rgba(92,126,37,.7)',
            ...A('gw-pop', 0.35),
          }}
        >
          <span
            style={{
              position: 'relative',
              width: 32,
              height: 32,
              display: 'block',
              animation: 'gw-float 3.4s ease-in-out infinite',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 14.5,
                width: 32,
                height: 3.6,
                borderRadius: 3,
                background: '#fff',
              }}
            />
            <span
              style={{
                position: 'absolute',
                left: 14.5,
                width: 3.6,
                height: 32,
                borderRadius: 3,
                background: '#fff',
              }}
            />
          </span>
        </div>
      </div>
    );
  }

  if (kind === 'launch') {
    return (
      <div style={stageStyle}>
        <div style={{ ...fieldStyle, justifyContent: 'space-between', ...A('gw-slide', 0.05) }}>
          <span>{'⛪'} St. Alban's Parish</span>
          <span style={{ color: C.muted3 }}>{'▾'}</span>
        </div>
        <div
          style={{
            ...fieldStyle,
            marginTop: 11,
            justifyContent: 'space-between',
            ...A('gw-slide', 0.22),
          }}
        >
          <span>facilitator@parish.nz</span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              color: C.green,
              fontWeight: 700,
              ...A('gw-pop', 0.55),
            }}
          >
            <span style={tick()}>{'✓'}</span>Facilitator
          </span>
        </div>
        <div
          style={{
            marginTop: 11,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            ...A('gw-slide', 0.4),
          }}
        >
          {['Warden', 'Vicar'].map((r) => (
            <span
              key={r}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '7px 10px',
                background: C.tint,
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                color: C.greenDk,
              }}
            >
              <span
                style={{
                  width: 13,
                  height: 13,
                  borderRadius: 4,
                  background: C.green,
                  color: '#fff',
                  fontSize: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {'✓'}
              </span>
              {r}
            </span>
          ))}
          <span
            style={{
              marginLeft: 'auto',
              padding: '8px 13px',
              background: C.green,
              color: '#fff',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              ...A('gw-pop', 0.75),
            }}
          >
            Add & send
          </span>
        </div>
      </div>
    );
  }

  if (kind === 'rule') {
    const rows: Array<[string, string, string, boolean, boolean]> = [
      ['✎', 'Wording fix', 'Same version', false, false],
      ['+', 'Add a question', 'Same version', false, true],
      ['⇅', 'Remove / reorder', 'New version', true, false],
    ];
    return (
      <div style={stageStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 2 }}>
          {rows.map((r, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                ...A('gw-slide', 0.08 + i * 0.16),
              }}
            >
              <span
                style={{
                  width: 27,
                  height: 27,
                  borderRadius: 8,
                  background: C.tint,
                  color: C.greenDk,
                  fontSize: 14,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 auto',
                }}
              >
                {r[0]}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#4b5560', width: 124 }}>
                {r[1]}
              </span>
              <span style={{ color: '#c5c9b6', fontSize: 16 }}>{'→'}</span>
              <span
                style={{
                  padding: '5px 11px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  ...(r[3]
                    ? { background: C.green, color: '#fff' }
                    : { background: C.note, color: C.greenDk, border: '1px solid #dce4c9' }),
                }}
              >
                {r[2]}
              </span>
              {r[4] && (
                <span style={{ fontSize: 11, color: C.muted3, fontWeight: 600 }}>
                  {'·'} confirm
                </span>
              )}
            </div>
          ))}
        </div>
        <div
          style={{
            position: 'absolute',
            left: 18,
            right: 18,
            bottom: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            paddingTop: 12,
            borderTop: `1px solid ${C.line3}`,
            fontSize: 12,
            fontWeight: 700,
            color: C.green,
            ...A('gw-rise', 0.75),
          }}
        >
          <span>{'🔒'}</span>Answers already given stay safe
        </div>
      </div>
    );
  }

  if (kind === 'editor') {
    return (
      <div style={stageStyle}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '40%',
            height: '100%',
            background: 'linear-gradient(90deg,transparent,rgba(92,126,37,.08),transparent)',
            animation: 'gw-sweep 2.6s ease-in-out .3s infinite',
          }}
        />
        <div style={{ ...fieldStyle, justifyContent: 'space-between', position: 'relative' }}>
          <span>
            <span style={{ color: C.muted3, fontWeight: 700 }}>Instrument </span>Church Health
          </span>
          <span style={{ color: C.muted3 }}>{'▾'}</span>
        </div>
        <div
          style={{
            ...fieldStyle,
            marginTop: 11,
            justifyContent: 'space-between',
            position: 'relative',
            ...A('gw-slide', 0.2),
          }}
        >
          <span>
            <span style={{ color: C.muted3, fontWeight: 700 }}>Editing from </span>2026.2 {'·'}{' '}
            latest
          </span>
          <span style={{ color: C.muted3 }}>{'▾'}</span>
        </div>
        <div
          style={{
            marginTop: 13,
            padding: 13,
            border: `1px solid ${C.line3}`,
            borderRadius: 10,
            background: C.field2,
            position: 'relative',
            ...A('gw-rise', 0.4),
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: '#b7bcaa',
            }}
          >
            Basis
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 3,
              background: '#e7e5d8',
              margin: '11px 0',
              width: '70%',
            }}
          />
          <div style={{ height: 6, borderRadius: 3, background: '#eeecdf', width: '45%' }} />
        </div>
      </div>
    );
  }

  if (kind === 'editing') {
    return (
      <div style={stageStyle}>
        <div
          style={{
            display: 'inline-block',
            padding: '3px 10px',
            background: C.tint,
            color: C.greenDk,
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '.04em',
            ...A('gw-pop', 0.05),
          }}
        >
          Leadership
        </div>
        <div
          style={{
            marginTop: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: 12,
            border: `1px solid ${C.line4}`,
            borderRadius: 10,
            background: C.field,
            ...A('gw-slide', 0.2),
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: C.tint,
              color: C.greenDk,
              fontSize: 12,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
            }}
          >
            3
          </span>
          <span style={{ flex: 1, fontSize: 13, color: '#4b5560', fontWeight: 600 }}>
            Our leaders model healthy conflict{'…'}
          </span>
          <span style={{ display: 'flex', gap: 10, color: '#b7bcaa', fontSize: 14 }}>
            {'↑'}
            {'↓'}
            <span style={{ color: '#c98' }}>{'✕'}</span>
          </span>
        </div>
        <div
          style={{
            marginTop: 10,
            marginLeft: 30,
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '11px 12px',
            border: '1px dashed #cdd2be',
            borderRadius: 10,
            background: C.field2,
            fontSize: 12.5,
            color: '#7d8676',
            fontWeight: 600,
            ...A('gw-slide', 0.55),
          }}
        >
          <span style={{ color: C.green, fontWeight: 800 }}>+</span>Follow-up {'·'} written answer
        </div>
      </div>
    );
  }

  if (kind === 'markings') {
    const flagged = 7;
    return (
      <div style={stageStyle}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: C.muted3, ...A('gw-rise', 0) }}>
          Child-safety question {'·'} 10 responses
        </div>
        <div style={{ display: 'flex', gap: 7, marginTop: 13, flexWrap: 'wrap' }}>
          {Array.from({ length: 10 }, (_, i) => {
            const bad = i === flagged;
            return (
              <span
                key={i}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(bad
                    ? {
                        background: C.redBg,
                        color: C.red,
                        animation: 'gw-pop .5s both, gw-pulse 1.8s ease-out 1.3s infinite',
                        animationDelay: `${0.1 + i * 0.05}s`,
                      }
                    : { background: C.tint, color: C.greenDk, ...A('gw-pop', 0.1 + i * 0.05) }),
                }}
              >
                {bad ? '1' : '4'}
              </span>
            );
          })}
        </div>
        <div
          style={{
            marginTop: 18,
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            ...A('gw-rise', 0.6),
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 700, color: C.muted2, width: 62 }}>Average</span>
          <div
            style={{
              flex: 1,
              height: 10,
              borderRadius: 5,
              background: '#f0eee3',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: '74%',
                background: C.green,
                borderRadius: 5,
                transformOrigin: 'left',
                animation: 'gw-growx .7s cubic-bezier(.4,0,.2,1) both',
                animationDelay: '.7s',
              }}
            />
          </div>
          <span style={{ fontSize: 14, fontWeight: 800, color: C.green }}>3.7</span>
        </div>
        <div
          style={{
            position: 'absolute',
            right: 16,
            top: 46,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '6px 11px',
            background: C.red,
            color: '#fff',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            boxShadow: '0 8px 18px -8px rgba(182,56,45,.8)',
            animation: 'gw-flag .5s both',
            animationDelay: '1.25s',
          }}
        >
          {'⚑'} Flagged
        </div>
      </div>
    );
  }

  if (kind === 'copy') {
    const src: Array<[string, boolean]> = [
      ['Leadership', true],
      ['Discipleship', false],
      ['Mission', true],
    ];
    const cardStyle: CSSProperties = {
      flex: 1,
      minWidth: 0,
      padding: 12,
      borderRadius: 10,
      background: C.field,
    };
    return (
      <div style={{ ...stageStyle, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ ...cardStyle, border: `1px solid ${C.line4}`, ...A('gw-rise', 0.05) }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: C.muted3,
              marginBottom: 9,
            }}
          >
            Source {'·'} v2
          </div>
          {src.map((r, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                margin: '8px 0',
                fontSize: 12.5,
                fontWeight: 600,
                color: '#4b5560',
              }}
            >
              <span
                style={{
                  width: 15,
                  height: 15,
                  borderRadius: 4,
                  flex: '0 0 auto',
                  ...(r[1]
                    ? {
                        background: C.green,
                        color: '#fff',
                        fontSize: 9,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }
                    : { border: '1.5px solid #cdd2be' }),
                }}
              >
                {r[1] ? '✓' : ''}
              </span>
              {r[0]}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 22, color: C.green, flex: '0 0 auto', ...A('gw-pop', 0.55) }}>
          {'→'}
        </div>
        <div
          style={{
            ...cardStyle,
            background: '#fff',
            border: '1px solid #d9e0c8',
            ...A('gw-rise', 0.2),
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: C.muted3,
              marginBottom: 9,
            }}
          >
            Your draft
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 3,
              background: '#eeecdf',
              margin: '8px 0',
              width: '80%',
            }}
          />
          <div
            style={{
              height: 6,
              borderRadius: 3,
              background: '#eeecdf',
              margin: '8px 0',
              width: '60%',
            }}
          />
          <div
            style={{
              marginTop: 10,
              padding: '8px 10px',
              background: C.tint,
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 700,
              color: C.greenDk,
              ...A('gw-pop', 0.8),
            }}
          >
            + Mission
          </div>
        </div>
      </div>
    );
  }

  if (kind === 'ingest') {
    const items: Array<[string, boolean]> = [
      ['▣ Section', true],
      ['scale question', false],
      ['written question', false],
      ['▣ Section', true],
      ['follow-up', false],
    ];
    return (
      <div style={{ ...stageStyle, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            flex: '0 0 auto',
            width: 128,
            height: 140,
            background: C.field2,
            border: `1px solid ${C.line4}`,
            borderRadius: 8,
            padding: 13,
            ...A('gw-rise', 0.05),
          }}
        >
          {[95, 80, 88, 60, 84, 50].map((w, i) => (
            <div
              key={i}
              style={{
                height: 5,
                borderRadius: 3,
                background: '#e7e5d8',
                margin: '9px 0',
                width: `${w}%`,
              }}
            />
          ))}
        </div>
        <div
          style={{
            fontSize: 20,
            color: C.green,
            flex: '0 0 auto',
            animation: 'gw-float 1.8s ease-in-out infinite',
          }}
        >
          {'→'}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
          {items.map((it, i) => (
            <div
              key={i}
              style={{
                padding: it[1] ? '7px 10px' : '6px 10px 6px 20px',
                borderRadius: 7,
                fontSize: 11.5,
                fontWeight: it[1] ? 700 : 600,
                ...(it[1]
                  ? { background: C.tint, color: C.greenDk }
                  : { background: C.field, color: '#7d8676', border: `1px solid ${C.line3}` }),
                ...A('gw-slide', 0.4 + i * 0.16),
              }}
            >
              {it[0]}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === 'publish') {
    const cards: Array<[string, string, boolean]> = [
      ['New version', 'v4', true],
      ['New instrument', '…', false],
    ];
    return (
      <div style={stageStyle}>
        <div style={{ display: 'flex', gap: 13 }}>
          {cards.map((c, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                padding: '16px 12px',
                borderRadius: 11,
                textAlign: 'center',
                ...(c[2]
                  ? { border: `2px solid ${C.green}`, background: C.note }
                  : { border: `1.5px solid ${C.line4}`, background: C.field }),
                ...A('gw-pop', 0.1 + i * 0.18),
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: c[2] ? C.greenDk : C.muted2 }}>
                {c[0]}
              </div>
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  margin: '7px 0 3px',
                  color: c[2] ? C.green : '#c5c9b6',
                }}
              >
                {c[1]}
              </div>
              {c[2] && (
                <div style={{ fontSize: 11, fontWeight: 700, color: C.green }}>{'✓'} suggested</div>
              )}
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: 12,
            background: C.field2,
            border: `1px solid ${C.line3}`,
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            color: '#4b5560',
            ...A('gw-rise', 0.55),
          }}
        >
          <span
            style={{
              width: 19,
              height: 19,
              borderRadius: 5,
              background: C.green,
              color: '#fff',
              fontSize: 11,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
            }}
          >
            {'✓'}
          </span>
          Use this version for newly created cycles
        </div>
      </div>
    );
  }

  if (kind === 'settings') {
    const rows: Array<[string, boolean | null]> = [
      ['Module switch', true],
      ['Automatic reminders', true],
      ['Anonymity thresholds', null],
    ];
    return (
      <div style={stageStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginTop: 8 }}>
          {rows.map((r, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '13px 14px',
                background: C.field,
                border: `1px solid ${C.line3}`,
                borderRadius: 10,
                ...A('gw-slide', 0.08 + i * 0.14),
              }}
            >
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#4b5560' }}>
                {r[0]}
              </span>
              {r[1] === null ? (
                <span style={{ color: '#c5c9b6', fontSize: 15 }}>{'▸'}</span>
              ) : (
                <span
                  style={{
                    width: 38,
                    height: 22,
                    borderRadius: 11,
                    background: C.green,
                    position: 'relative',
                    flex: '0 0 auto',
                    ...A('gw-pop', 0.3 + i * 0.14),
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2.5,
                      left: 18,
                      width: 17,
                      height: 17,
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,.2)',
                    }}
                  />
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // faq
  const qs: Array<[string, boolean]> = [
    ['Fix a typo in a published version?', true],
    ['Add safeguarding markings to an older version?', false],
    ['How often can I ingest documents?', false],
  ];
  return (
    <div style={stageStyle}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
        {qs.map((q, i) => (
          <div
            key={i}
            style={{
              border: `1px solid ${C.line3}`,
              borderRadius: 10,
              overflow: 'hidden',
              background: q[1] ? C.note : C.field,
              ...A('gw-rise', 0.06 + i * 0.12),
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 14px',
                fontSize: 13,
                fontWeight: 700,
                color: C.ink,
              }}
            >
              <span style={{ flex: 1 }}>{q[0]}</span>
              <span
                style={{ color: C.green, fontSize: 14, transform: q[1] ? 'rotate(90deg)' : 'none' }}
              >
                {'▸'}
              </span>
            </div>
            {q[1] && (
              <div
                style={{ padding: '0 14px 13px', fontSize: 12.5, lineHeight: 1.5, color: C.muted }}
              >
                Yes — fix the wording and {'“'}Correct wording{'”'} saves it onto the same version
                in place. Only removing or reordering questions needs a new version.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── The tour ─────────────────────────────────────────────────────────────────
export function GuideWalkthrough(): JSX.Element {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const total = STEPS.length;
  const step = STEPS[i];

  // A timeout per step while playing keeps the countdown bar and the advance in
  // lockstep; it re-arms whenever the step or play state changes.
  useEffect(() => {
    if (!playing) return;
    const t = window.setTimeout(() => setI((x) => (x + 1) % total), AUTOPLAY_SECONDS * 1000);
    return () => window.clearTimeout(t);
  }, [playing, i, total]);

  const go = (n: number): void => setI(((n % total) + total) % total);
  const pct = total > 1 ? (i / (total - 1)) * 100 : 0;

  if (!step) return <></>; // unreachable: i is always clamped into STEPS

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 720,
        margin: '0 auto',
        background: C.paper,
        border: `1px solid ${C.line}`,
        borderRadius: 18,
        overflow: 'hidden',
        boxShadow: '0 22px 52px -32px rgba(43,51,66,.55)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 18px',
          background: C.green,
          color: '#fff',
        }}
      >
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? 'Pause the tour' : 'Play the tour'}
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            border: 'none',
            background: '#fff',
            color: C.green,
            fontSize: 13,
            fontWeight: 800,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
            letterSpacing: '-1px',
          }}
        >
          {playing ? '❙❙' : '▶'}
        </button>
        <div style={{ lineHeight: 1.15 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            Church Health Assessment {'·'} Guided tour
          </div>
          <div style={{ fontSize: 11.5, opacity: 0.85 }}>
            {playing ? 'Playing · tap to pause' : 'Paused · tap to play'}
          </div>
        </div>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 12.5,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            opacity: 0.92,
          }}
        >
          {String(i + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
      </div>

      {/* Progress */}
      <div style={{ height: 3, background: C.line, overflow: 'hidden' }}>
        {playing ? (
          <div
            key={i}
            style={{
              height: '100%',
              background: C.green,
              transformOrigin: 'left',
              animation: `gw-growx ${AUTOPLAY_SECONDS}s linear both`,
            }}
          />
        ) : (
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              background: C.green,
              transition: 'width .45s cubic-bezier(.4,0,.2,1)',
            }}
          />
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '22px 26px 8px' }}>
        <div key={i}>
          <Art kind={step.art} />
        </div>
        <div
          style={{
            marginTop: 18,
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: '.13em',
            textTransform: 'uppercase',
            color: C.green,
          }}
        >
          {step.kicker}
        </div>
        <h3
          style={{
            margin: '6px 0 0',
            fontSize: 23,
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-.015em',
            color: C.ink,
            textWrap: 'balance',
          }}
        >
          {step.title}
        </h3>
        <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: C.body }}>
          {segs(step.lead)}
        </p>
        {step.points && (
          <ul
            style={{
              listStyle: 'none',
              margin: '15px 0 0',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {step.points.map((p, k) => (
              <li
                key={k}
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  ...A('gw-rise', 0.05 + k * 0.07, '0.5s'),
                }}
              >
                {p.n ? (
                  <span
                    style={{
                      flex: '0 0 auto',
                      width: 23,
                      height: 23,
                      borderRadius: '50%',
                      background: C.tint,
                      color: C.greenDk,
                      fontSize: 13,
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: 1,
                    }}
                  >
                    {p.n}
                  </span>
                ) : (
                  <span
                    style={{
                      flex: '0 0 auto',
                      width: 23,
                      height: 23,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: 1,
                    }}
                  >
                    <span
                      style={{ width: 6, height: 6, borderRadius: '50%', background: C.green }}
                    />
                  </span>
                )}
                <span style={{ flex: 1, fontSize: 14.5, lineHeight: 1.55, color: C.body }}>
                  {segs(p.seg)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {step.note && (
          <div
            style={{
              marginTop: 16,
              background: C.note,
              borderLeft: `3px solid ${C.green}`,
              borderRadius: '0 8px 8px 0',
              padding: '12px 15px',
              fontSize: 13.5,
              lineHeight: 1.5,
              color: '#4b5560',
              ...A('gw-rise', 0.35, '0.5s'),
            }}
          >
            {segs(step.note)}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '15px 20px',
          borderTop: `1px solid ${C.line2}`,
          marginTop: 10,
        }}
      >
        <button
          type="button"
          onClick={() => go(i - 1)}
          style={{
            padding: '9px 15px',
            border: '1px solid #e0ddd0',
            borderRadius: 9,
            background: '#fff',
            color: '#565f6e',
            fontSize: 13.5,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {'←'} Back
        </button>
        <div
          style={{
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {STEPS.map((s, k) => (
            <button
              key={k}
              type="button"
              onClick={() => go(k)}
              title={s.short}
              aria-label={s.short}
              aria-current={k === i ? 'step' : undefined}
              style={{
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                height: 8,
                borderRadius: 4,
                transition: 'all .3s',
                width: k === i ? 22 : 8,
                background: k === i ? C.green : '#d3d6c6',
              }}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => go(i + 1)}
          style={{
            padding: '9px 17px',
            border: 'none',
            borderRadius: 9,
            background: C.green,
            color: '#fff',
            fontSize: 13.5,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {i >= total - 1 ? 'Start over' : 'Next →'}
        </button>
      </div>
    </div>
  );
}
